import React, { useState } from "react";
import { PrimaryButton } from "components/designV2/buttons/primaryButton";
import { SecondaryButton } from "components/designV2/buttons/secondaryButton";
import { Modal } from "components/designV2/modal/modal";
import { useTranslation } from "react-i18next";
import {
  AlertModalActions,
  AlertModalContent,
  AlertModalMessage,
} from "./alertModal.styled";

type AlertModaProps = {
  children: (
    openAlertModal: (
      text: string | React.ReactNode,
      showCancel?: boolean,
      onConfirm?: (close: () => void) => void,
      confirmText?: string,
      onSecondaryConfirm?: (close: () => void) => void,
      secondaryConfirmText?: string,
    ) => void,
  ) => React.ReactNode;
};

export const AlertModal: React.FC<AlertModaProps> = ({ children }) => {
  const { t } = useTranslation();
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [modalMessage, setModalMessage] = useState<string | React.ReactNode>(
    "",
  );
  const [showCancelButton, setShowCancelButton] = useState(false);
  const [primaryAction, setPrimaryAction] = useState<
    ((close: () => void) => void) | null
  >(null);
  const [secondaryAction, setSecondaryAction] = useState<
    ((close: () => void) => void) | null
  >(null);
  const [primaryButtonText, setPrimaryButtonText] = useState<
    string | undefined
  >();
  const [secondaryButtonText, setSecondaryButtonText] = useState<
    string | undefined
  >();

  const closeModal = (): void => setIsModalOpen(false);

  const openAlertModal = (
    message: string | React.ReactNode,
    showCancel = false,
    onConfirm?: (close: () => void) => void,
    confirmText?: string,
    onSecondaryConfirm?: (close: () => void) => void,
    secondaryText?: string,
  ): void => {
    setModalMessage(message);
    setShowCancelButton(showCancel);
    setPrimaryAction(() => onConfirm || null);
    setSecondaryAction(() => onSecondaryConfirm || null);
    setPrimaryButtonText(confirmText);
    setSecondaryButtonText(secondaryText);
    setIsModalOpen(true);
  };

  return (
    <>
      {children(openAlertModal)}
      <Modal
        isOpen={isModalOpen}
        onClose={closeModal}
        label={t("General.Information")}
        size="compact"
        footer={
          <AlertModalActions>
            {showCancelButton && (
              <SecondaryButton
                label={secondaryButtonText ?? t("General.Cancel")}
                onClick={() => {
                  if (secondaryAction) {
                    secondaryAction(closeModal);
                    return;
                  }

                  closeModal();
                }}
              />
            )}
            <PrimaryButton
              label={primaryButtonText ?? t("General.Ok")}
              onClick={() => {
                if (primaryAction) {
                  primaryAction(closeModal);
                  return;
                }

                closeModal();
              }}
            />
          </AlertModalActions>
        }
      >
        <AlertModalContent>
          {typeof modalMessage === "string" ? (
            <AlertModalMessage>{modalMessage}</AlertModalMessage>
          ) : (
            modalMessage
          )}
        </AlertModalContent>
      </Modal>
    </>
  );
};
